import { FolderIcon, PlusIcon, TableIcon } from 'lucide-react'
import { GetCollectionByIdResult } from '@indigohive/cogfy-types/endpoints/getCollectionById'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCogfy, useCollections, usePermissions, useToasts, useUserEvent } from '../../hooks'
import { useMutation } from '@tanstack/react-query'
import { CreateCollectionCommand } from '@indigohive/cogfy-types/endpoints/createCollection'

export type FolderCollectionPageProps = {
  collection: GetCollectionByIdResult
}

export function FolderCollectionPage (props: FolderCollectionPageProps) {
  const { collection } = props
  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!
  const { t } = useTranslation()
  const permissions = usePermissions()
  const canCreateCollection = permissions.isAdmin
  const registerUserEvent = useUserEvent()
  const toasts = useToasts()
  const cogfy = useCogfy()
  const navigate = useNavigate()
  const getCollectionChildren = useCollections({ parentId: collection.id })

  const createCollection = useMutation({
    mutationFn: (data: CreateCollectionCommand) => cogfy.createCollection(data),
    onSuccess: result => {
      navigate(`/${workspace}/${result.id}`)
      toasts.success(t('collectionTemplatesPage:Collection created'))
    },
    onError: () => {
      toasts.error(t('collectionTemplatesPage:Error creating collection'))
    }
  })

  return (
    <div className="px-4">
      {getCollectionChildren.error && 'Error'}
      {getCollectionChildren.isLoading && <progress className="progress" />}
      <ul className="menu w-full">
        {getCollectionChildren.data?.data.length === 0 && (
          <li>
            <a onClick={() => createCollection.mutate({ parentId: collection.id })}>
              <PlusIcon size={20} />
              {canCreateCollection ? t('collectionAndFolderActions:New empty collection') : t('No collections found')}
            </a>
          </li>
        )}
        {getCollectionChildren.data && getCollectionChildren.data.data.length > 0 && (
          <>
            {getCollectionChildren.data.data.map(collection => (
              <li key={collection.id}>
                <Link
                  to={`/${workspace}/${collection.id}`}
                  onClick={() => {
                    registerUserEvent.mutate({
                      type: 'collection_selected',
                      data: {
                        collectionId: collection.id,
                        name: collection.name,
                        type: collection.type,
                        from: 'list'
                      }
                    })
                  }}
                >
                  {collection.type === 'database'
                    ? <TableIcon size={16} />
                    : <FolderIcon size={16} />}
                  {collection.name ?? t('Untitled')}
                </Link>
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  )
}
